import { inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpRequest,
  HttpHandlerFn
} from '@angular/common/http';
import { catchError, map, throwError } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AccessService } from '../service/access.service';
import { localDB } from '../../shared/helpers/localStorage';
import { GoogleService } from '../service/googleApi.service';
import { environment as env } from 'src/environments/environment';
import { Router } from '@angular/router';
import { CacheService } from '../service/cache.service';
import { CookiesService } from '../service/cookies.service';
import { NgZone, Injector } from '@angular/core';
import { desencriptar, encriptar } from 'src/app/shared/helpers/encryption';

export const InterceptorFn: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const router = inject(Router);
  const cacheService = inject(CacheService);
  const cookiesService = inject(CookiesService);
  const ngZone = inject(NgZone);
  const googleService = inject(GoogleService);
  const injector = inject(Injector);

  const dialog = injector.get(MatDialog, null);

  const token: string = localDB.getItem('token');
  const authType: string = localDB.getItem('authType');
  const remoteHost = req.url.split('/')[2];

  let request = setRequest(token, authType, remoteHost, req);

  if (request.responseType !== 'blob' &&
    !isAuthEndpoint(request.url)) {
    request = request.clone({
      responseType: 'text'
    });
  }

  if (
    request.responseType !== 'blob' &&
    !isGoogleApi(remoteHost) &&
    remoteHost !== env.awsBucked &&
    !isAuthEndpoint(request.url)
  ) {
    request = request.clone({
      responseType: 'text' as 'json'
    });
  }


  if (
    request.body instanceof FormData &&
    request.responseType !== 'blob' &&
    !isGoogleApi(remoteHost) &&
    remoteHost !== env.awsBucked &&
    !request.headers.has('noUse') &&
    !isAuthEndpoint(request.url)
  ) {
    const form = request.body as FormData;
    const metadata: any = {};
    form.forEach((value, key) => {
      if (!(value instanceof File)) {
        const values = form.getAll(key);
        metadata[key] = values.length > 1 ? JSON.stringify(values) : values[0].toString();
        form.delete(key);
      }
    });

    if (Object.keys(metadata).length > 0) {
      const encrypted = encriptar(env.securityKey, JSON.stringify(metadata));
      form.append('encrypted_payload', encrypted);
    }

    request = request.clone({
      body: form
    });
  }


  if (
    request.body &&
    !(request.body instanceof FormData) &&
    request.responseType !== 'blob' &&
    !isGoogleApi(remoteHost) &&
    remoteHost !== env.awsBucked &&
    !request.headers.has('noUse') &&
    !isAuthEndpoint(request.url)
  ) {
    const encrypted = encriptar(env.securityKey, JSON.stringify(request.body));

    request = request.clone({
      body: encrypted,
      setHeaders: {
        'Content-Type': 'text/plain'
      }
    });
  }

  return next(request).pipe(
    map((event: any) => {
      if (request.responseType === 'blob') {
        return event;
      }
      // If backend returns encrypted full body
      if (typeof event.body === 'string') {
        const body = tryDecrypt(event.body) ?? tryParse(event.body);
        return body ? event.clone({ body }) : event;
      }

      return event;
    }),

    catchError((err: HttpErrorResponse) => {
      if (err.status === 401) {
        if (isGoogleApi(remoteHost)) {
          googleService.updateValueToken('');
          localDB.removeItem('accessToken');
        } else {
          console.warn('Token inválido o expirado. Cerrando sesión...');
          cerrarSesion(ngZone, cookiesService, cacheService);
          const accessService = inject(AccessService);
          accessService.routeLogin?.();
        }
      }

      if (err.status === 403) {
        if (isGoogleApi(remoteHost)) {
          googleService.updateValueToken('');
          localDB.removeItem('accessToken');
        } else {
          swalError403(remoteHost);
        }
      }

      return throwError(() => err);
    })
  );
};

/* -------------------- Funciones auxiliares -------------------- */

function setRequest(
  token: string,
  authType: string,
  remoteHost: string,
  req: HttpRequest<unknown>
): HttpRequest<unknown> {
  let request = req;
  const noUse = req.headers.has('noUse');

  if (!noUse && token) {
    switch (remoteHost) {
      case env.awsBucked:
        request = req.clone({
          setHeaders: {
            Accept: 'image/webp,*/*,application/pdf'
          },
          responseType: 'blob'
        });
        break;

      case 'www.googleapis.com':
      case 'docs.googleapis.com':
        request = req.clone({
          setHeaders: {
            authorization: `Bearer ${localDB.getItem('accessToken')}`,
          }
        });
        break;

      default:
        if (skipInterceptor(remoteHost)) {
          request = req.clone({});
        } else {
          request = req.clone({
            setHeaders: {
              authorization: `Bearer ${token}`,
              auth_Type: authType
            }
          });
        }
        break;
    }
  }

  return request;
}

function cerrarSesion(
  ngZone: NgZone,
  cookiesService: CookiesService,
  cacheService: CacheService
): void {
  ngZone.run(async () => {
    try {
      if (cookiesService.cookieNameEnv) {
        cookiesService.deleteCookie(cookiesService.cookieNameEnv);
      }

      localStorage.clear();

      cacheService.refreshAllValues().catch(err =>
        console.error('Error limpiando cache:', err)
      );

      window.location.href = '/auth/login';
    } catch {
      localStorage.clear();
      window.location.href = '/auth/login';
    }
  });
}

function swalError403(remoteHost: string) {
  if (remoteHost === env.awsBucked) {
  } else {
  }
}

function isGoogleApi(host: string): boolean {
  return host.startsWith('www.googleapis.com') || host.startsWith('docs.googleapis.com');
}

function skipInterceptor(host: string): boolean {
  return host.includes('appsmartbucket');
}

function isAuthEndpoint(url: string): boolean {
  return url.includes('/api/login') ||
    url.includes('/api/userState') ||
    url.includes('/api/SetPassword') ||
    url.includes('/api/ConfirmCode') ||
    url.includes('/api/MFAChallenge') ||
    url.includes('/api/MFAPreference') ||
    url.includes('/api/verify') ||
    url.includes('/api/logout') ||
    url.includes('/api/recover-password') ||
    url.includes('/api/reset-password') ||
    url.includes('/api/configuraciones/get_encrypt_env') ||
    url.includes('/api/configuraciones/getpubkey') ||
    url.includes('/api/configuraciones/receive_encrypted_key') ||
    url.includes('/api/configuraciones/decrypt_env') ||
    url.includes('/api/claims/siniestros/upload-url') ||
    url.includes('/api/claims/siniestro/bulkTrigger') ||
    url.includes('/api/claims/siniestros/procesar') ||
    url.includes('api/informe-medico/descargar-pdf');
}



function tryDecrypt(raw: string): any | null {
  try {
    const decrypted = desencriptar(env.securityKey, raw);
    const parsed = JSON.parse(decrypted);
    return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
  } catch {
    return null;
  }
}

function tryParse(raw: string): any | null {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}